<script setup>
import { useGlobalStore } from '@stores/global'
import { ref, watch } from 'vue'
import { gsap } from 'gsap'
import TransitionFade from '@components/global/transitions/TransitionFade.vue'

const global = useGlobalStore()
const props = defineProps(['modelValue', 'filter', 'type', 'required', 'placeholder', 'disabled', 'max', 'label', 'sublabel', 'icon', 'error', 'autocomplete', 'spellcheck', 'autocapitalize', 'forgot'])
const emit = defineEmits(['update:modelValue'])

const input = ref(null)
const ov = ref(null)

watch(
	() => props.error,
	(newVal, oldVal) => {
		if (newVal && newVal !== oldVal && input.value) {
			gsap.timeline()
				.to(input.value, { x: -7, duration: 0.06, ease: 'power2.out' })
				.to(input.value, { x: 7, duration: 0.06, ease: 'power2.out' })
				.to(input.value, { x: -5, duration: 0.06, ease: 'power2.out' })
				.to(input.value, { x: 5, duration: 0.06, ease: 'power2.out' })
				.to(input.value, { x: -3, duration: 0.06, ease: 'power2.out' })
				.to(input.value, { x: 0, duration: 0.06, ease: 'power2.inOut' })
		}
	},
)
const oss = ref(null)
const ose = ref(null)
const handlers = ref({
	input: handleInput,
	keyup: handleInput,
	keydown: handleInput,
	select: handleInput,
	contextmenu: handleInput,
	drop: handleInput,
})

function handleInput(event) {
	if (props.filter == 'username') {
		if (/^[a-zA-Z0-9_ ]*$/i.test(input.value.value)) {
			ov.value = input.value.value.replace(/-+/g, '').replace(/\s+/g, '_').replace(/\_\_+/g, '_').replace(/^_+/, '')
			oss.value = input.value.selectionStart
			ose.value = input.value.selectionEnd
			input.value.value = ov.value
			emit('update:modelValue', ov.value)
		} else if (ov.value) {
			input.value.value = ov.value
			emit('update:modelValue', ov.value)
			input.value.setSelectionRange(oss.value, ose.value)
		} else {
			input.value.value = ''
			emit('update:modelValue', '')
		}
	} else if (props.filter == 'phone') {
		if (/^[0-9+()-]*$/i.test(input.value.value)) {
			ov.value = input.value.value.replace(/\s+/g, '-').replace(/\-\-+/g, '-').replace(/^-+/, '')
			oss.value = input.value.selectionStart
			ose.value = input.value.selectionEnd
			input.value.value = ov.value
			emit('update:modelValue', ov.value)
		} else if (ov.value) {
			input.value.value = ov.value
			emit('update:modelValue', ov.value)
			input.setSelectionRange(oss.value, ose.value)
		} else {
			input.value.value = ''
			emit('update:modelValue', '')
		}
	} else if (props.filter == 'numeric') {
		if (/^[0-9,]*$/i.test(input.value.value)) {
			// Remove existing commas to get the raw number
			const rawValue = input.value.value.replace(/,/g, '')
			// Add commas for thousands separator
			const formattedValue = rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
			ov.value = formattedValue
			oss.value = input.value.selectionStart
			ose.value = input.value.selectionEnd
			input.value.value = ov.value
			emit('update:modelValue', rawValue)
		} else if (ov.value) {
			input.value.value = ov.value
			const rawValue = ov.value.replace(/,/g, '')
			emit('update:modelValue', rawValue)
			input.value.setSelectionRange(oss.value, ose.value)
		} else {
			input.value.value = ''
			emit('update:modelValue', '')
		}
	} else {
		emit('update:modelValue', event.target.value)
	}
}
</script>

<template>
	<div class="flex w-full flex-col gap-2 normal-case">
		<div class="flex justify-between" v-if="label">
			<label class="flex items-center gap-1 text-sm text-crowns-off-white" v-if="label">
				{{ label }}
				<span class="text-[.65rem]" v-if="sublabel">{{ sublabel }}</span>
			</label>
			<!-- <span class="label-medium cursor-pointer text-crowns-castle-shadow" @click="global.openModal(['forgot-password'])" v-if="forgot">Forgot password?</span> -->
		</div>
		<div class="relative flex w-full">
			<i :class="'absolute left-4 top-1/2 -translate-y-1/2 text-sm text-neutral-500 ' + icon"></i>
			<input :class="{ 'icon-padding': icon }" :type="type || 'text'" :placeholder="placeholder" :maxlength="max" :required="required" v-on="handlers" :value="modelValue" :autocomplete="autocomplete" :disabled="disabled" :spellcheck="spellcheck" :autocapitalize="autocapitalize" :inputmode="filter == 'numeric' ? 'numeric' : 'text'" ref="input" />
		</div>
		<TransitionFade>
			<p class="text-xs text-red-500" v-if="error">
				{{ error }}
			</p>
		</TransitionFade>
	</div>
</template>

<style scoped>
input {
	@apply w-full grow border border-crowns-grey bg-crowns-dark-grey px-2 py-3 text-sm normal-case placeholder:normal-case placeholder:text-crowns-castle-stone focus:border-[#2c2c2c] focus:outline-none;
}
.light input {
	@apply border-crowns-clouds bg-[#c4c4c4] text-crowns-off-black focus:border-[#949494];
}
.icon-padding {
	@apply pl-9;
}
</style>
